import { useCallback, useEffect, useRef, type ChangeEvent } from 'react';
import { normalizeOtpCode } from '../login/emailOtpAuth';
import { useSmsOtpAutofill } from '../login/useSmsOtpAutofill';
import styles from '../pages/AuthLoginPortal.module.css';

type AuthOtpCodeFieldProps = {
  id: string;
  label: string;
  value: string;
  disabled?: boolean;
  placeholder: string;
  onChange: (value: string) => void;
};

/**
 * Single OTP input wired for SMS/email autofill:
 * - autocomplete=one-time-code (iOS suggestion bar / Android autofill)
 * - autofocus when mounted (paste / autofill targets this field)
 * - WebOTP API on Chrome Android when SMS includes @host #code
 * - paste of full SMS text extracts the digit run
 */
export function AuthOtpCodeField({
  id,
  label,
  value,
  disabled = false,
  placeholder,
  onChange,
}: AuthOtpCodeFieldProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const applyCode = useCallback(
    (raw: string) => {
      onChange(normalizeOtpCode(raw));
    },
    [onChange],
  );

  useSmsOtpAutofill({
    enabled: !disabled,
    onCode: applyCode,
  });

  useEffect(() => {
    const el = inputRef.current;
    if (!el || disabled) {
      return;
    }
    // Defer so the code step has painted before focusing (iOS autofill hint).
    const timer = window.setTimeout(() => {
      el.focus({ preventScroll: true });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [disabled]);

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    applyCode(event.target.value);
  };

  return (
    <div className={styles.fieldBlock}>
      <label className={styles.fieldLabel} htmlFor={id}>
        {label}
      </label>
      <input
        ref={inputRef}
        id={id}
        name="one-time-code"
        className={styles.textInput}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        enterKeyHint="done"
        maxLength={8}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={onInputChange}
      />
    </div>
  );
}
